import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  PARAM_KEYS,
  PL, ADVANCED_PL, advancedUnit,
  PARAM_UNITS,
  PARAM_UNIT_SHORT,
  PARAM_AGG,
  aggregateStageInputs,
  buildCountryInferredRows,
  HIDDEN_FROM_TABLE_SUBSTAGES,
} from "@/lib/reportUtils";

const rawValueFormatter = new Intl.NumberFormat("en-US", {
  useGrouping: false,
  maximumFractionDigits: 4,
});

const formatRawValue = (value) => {
  if (value == null || value === "") return "—";
  return typeof value === "number" ? rawValueFormatter.format(value) : value;
};

export const InputAudit = ({ data, paramsByStage, mode, countryData }) => {
  const bom = data?.results?.mass_fractions || data?.bom_items || [];
  const stageInputs = data?.stage_inputs || {};
  const transparency = data?.transparency;
  const isAdvanced = mode === "advanced";
  const unitSummary = data?.results?.unit_summary;
  const unitAudit = unitSummary?.audit;

  // Country-average inferred rows for Raw-Material-Extraction, Raw-Material-
  // Processing and EOL-Disposal. Weighted by BOM mass fraction. These feed
  // the cumulated totals but are intentionally omitted from the table.
  const inferredRows = buildCountryInferredRows(bom, countryData || {});

  // Horizontal aggregation (sum / avg) including inferred country data
  const fallbackAgg = aggregateStageInputs(stageInputs, inferredRows);
  const agg = PARAM_KEYS.reduce((out, pk) => {
    const backend = unitSummary?.parameter_aggregates?.[pk];
    out[pk] = backend ? {
      value: backend.display_value,
      method: backend.method,
      count: backend.count,
      unit: backend.display_unit,
      reconciliation: backend.reconciliation,
    } : fallbackAgg[pk];
    return out;
  }, {});

  // Hide raw_material_extraction / raw_material_processing / eol_disposal
  // from the displayed columns (advanced only — basic uses different keys).
  const displayedStageKeys = Object.keys(stageInputs).filter(
    (k) => !HIDDEN_FROM_TABLE_SUBSTAGES.includes(k)
  );

  const phaseLabelMap = {
    extraction: "Raw Material Extraction",
    processing: "Raw Material Processing",
    disposal: "End-of-Life Disposal",
  };
  const stageInputLabelMap = {
    manufacturing_pre_processing: "Manufacturing Pre-Processing",
    manufacturing_production: "Manufacturing Production",
    use_phase_operation: "Use Phase Operation",
    use_phase_maintenance: "Use Phase Maintenance",
    eol_recycling: "EOL Recycling",
    eol_recovery: "EOL Recovery",
    logistics_transportation: "Logistics Transport",
    logistics_storage: "Logistics Storage",
  };

  return (
    <div className="space-y-5" data-testid="input-audit">
      {/* BOM */}
      <div>
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#617364] mb-2">Bill of Materials ({bom.length} components)</p>
        <Table>
          <TableHeader><TableRow className="border-[#E0E6E1]">
            <TableHead className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold">Material</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold">Country</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold">Component</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold text-right">Qty</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold text-right">Unit Weight (Kg)</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold text-right">Line Mass (Kg)</TableHead>
            <TableHead className="text-[10px] uppercase tracking-[0.1em] text-[#617364] font-bold text-right">Mass Share</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {bom.map((it, i) => (
              <TableRow key={i} className="border-[#E0E6E1]">
                <TableCell><Badge variant="outline" className="border-[#81C784] text-[#43A047]">{it.material}</Badge></TableCell>
                <TableCell className="text-[11px] text-[#617364]">{it.country || "—"}</TableCell>
                <TableCell className="text-[11px] text-[#1B291D]">{it.component || "—"}</TableCell>
                <TableCell className="text-right text-[11px]">{it.quantity ?? 1}</TableCell>
                <TableCell className="text-right text-[11px]">{typeof it.weight_kg === "number" ? it.weight_kg.toFixed(4) : it.weight_kg}</TableCell>
                <TableCell className="text-right text-[11px]">{Number(it.line_mass_kg ?? ((Number(it.quantity) || 1) * (Number(it.weight_kg) || 0))).toFixed(4)}</TableCell>
                <TableCell className="text-right text-[11px] font-medium text-[#43A047]">{it.mass_fraction != null ? (it.mass_fraction * 100).toFixed(2) + "%" : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Stage Inputs with horizontal aggregation (summary tiles + table) */}
      {Object.keys(stageInputs).length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#617364] mb-2">
            Raw Stage Inputs (values entered by user) — aggregated across {isAdvanced ? "substages" : "stages"}
          </p>

          {/* Summary tiles moved to Section 2 (Impact Summary) for storytelling
              flow. The aggregation column to the right of this table preserves
              the same numbers per parameter. */}

          <div className="overflow-x-auto">
            <table
              className="w-full border-collapse text-[10px]"
              data-testid="raw-input-table"
            >
              <thead>
                <tr className="bg-[#F8F9F8]">
                  <th className="text-left py-1.5 px-2 font-bold text-[#617364] uppercase tracking-wider border border-[#E0E6E1]">
                    Parameter
                  </th>
                  <th className="text-left py-1.5 px-2 font-bold text-[#617364] uppercase tracking-wider border border-[#E0E6E1]">
                    Unit
                  </th>
                  {displayedStageKeys.map((k) => (
                    <th
                      key={k}
                      className="py-1.5 px-2 font-bold text-[#617364] uppercase tracking-wider border border-[#E0E6E1] text-center"
                    >
                      {stageInputLabelMap[k] || k.replace(/_/g, " ")}
                    </th>
                  ))}
                  <th
                    className="py-1.5 px-2 font-bold text-[#1B291D] uppercase tracking-wider border border-[#E0E6E1] text-center bg-[#F0F4F1]"
                    data-testid="agg-header"
                  >
                    Total / Avg
                  </th>
                </tr>
              </thead>
              <tbody>
                {PARAM_KEYS.map((pk) => {
                  const a = agg[pk] || { value: 0, method: PARAM_AGG[pk], count: 0 };
                  const isSum = a.method === "sum";
                  return (
                    <tr key={pk}>
                      <td className="py-1 px-2 font-medium text-[#1B291D] border border-[#E0E6E1]">
                        {(isAdvanced ? ADVANCED_PL : PL)[pk]}
                      </td>
                      <td className="py-1 px-2 text-[#617364] border border-[#E0E6E1] italic whitespace-nowrap">
                        {isAdvanced ? advancedUnit(unitSummary?.audit?.canonical_units?.[pk] || PARAM_UNITS[pk]) : (unitSummary?.audit?.canonical_units?.[pk] || PARAM_UNITS[pk])}
                      </td>
                      {displayedStageKeys.map((k) => (
                        <td
                          key={k}
                          className="text-center py-1 px-2 border border-[#E0E6E1]"
                        >
                          {formatRawValue(stageInputs[k]?.[pk])}
                        </td>
                      ))}
                      <td
                        className="text-center py-1 px-2 border border-[#E0E6E1] bg-[#F0F4F1] font-semibold text-[#1B291D]"
                        data-testid={`agg-${pk}`}
                      >
                        <span className="text-[9px] text-[#617364] mr-1 uppercase">
                          {isSum ? "Σ" : "μ"}
                        </span>
                        {a.value == null ? "N/A / separate metrics" : a.value.toFixed(2)}
                        <span className="text-[9px] text-[#617364] ml-1">
                          {a.unit || PARAM_UNIT_SHORT[pk]}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-[#617364] mt-1">
            <strong>Aggregation:</strong> The saved method determines each sum or mean. Advanced 2.0 sums physical GWP, water, pollution points and allocated cost. Time is mean substage duration, not total elapsed time. Ecosystem damage and management performance are shown separately.
            {inferredRows.length > 0 && (
              <>
                {" "}The <strong>Total / Avg</strong> column and summary tiles also incorporate country-average benchmark
                values for{" "}
                {inferredRows
                  .map((r) => phaseLabelMap[r._source] || r._source)
                  .join(", ")}{" "}
                (mass-fraction weighted across the BOM), so they reflect the complete
                lifecycle even where the user did not enter those substages.
              </>
            )}
            {" "}Values shown are raw user inputs; the scoring engine normalises them against benchmarks separately before computing Parameter Avg. / ESGE (unchanged).
          </p>
        </div>
      )}

      {unitAudit && (
        <div className="p-3 rounded-lg bg-[#F8F9F8] border border-[#E0E6E1]" data-testid="unit-validation-audit">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#617364]">Unit Validation &amp; Normalization</p>
            <Badge variant="outline" className={unitAudit.issues?.some(i => i.severity === "error") ? "border-[#D32F2F] text-[#D32F2F]" : "border-[#43A047] text-[#388E3C]"}>
              {unitAudit.issues?.length || 0} issue{unitAudit.issues?.length === 1 ? "" : "s"}
            </Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] mb-3">
            <div><span className="text-[#617364]">Functional unit</span><strong className="block">{unitAudit.functional_unit?.label}</strong></div>
            <div><span className="text-[#617364]">Declared mass</span><strong className="block">{unitAudit.declared_product_mass_kg ?? "—"} Kg</strong></div>
            <div><span className="text-[#617364]">BOM mass</span><strong className="block">{Number(unitAudit.bom_total_mass_kg || 0).toFixed(4)} Kg</strong></div>
            <div><span className="text-[#617364]">Mass used</span><strong className="block">{Number(unitAudit.effective_product_mass_kg || 0).toFixed(4)} Kg</strong></div>
          </div>
          {(unitAudit.issues?.length || 0) > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[9px]" data-testid="unit-issues-table">
                <thead><tr>
                  {['Issue', 'Current value + unit', 'Expected unit', 'Required conversion', 'Corrected value', 'File / function'].map(h => <th key={h} className="text-left p-1.5 border border-[#E0E6E1]">{h}</th>)}
                </tr></thead>
                <tbody>{unitAudit.issues.map((issue, idx) => (
                  <tr key={`${issue.code}-${idx}`}>
                    <td className="p-1.5 border border-[#E0E6E1] font-semibold">{issue.code.replace(/_/g, " ")}</td>
                    <td className="p-1.5 border border-[#E0E6E1]">{issue.current_value ?? "—"} {issue.current_unit || "(missing)"}</td>
                    <td className="p-1.5 border border-[#E0E6E1]">{issue.expected_unit || "—"}</td>
                    <td className="p-1.5 border border-[#E0E6E1]">{issue.required_conversion}</td>
                    <td className="p-1.5 border border-[#E0E6E1]">{issue.corrected_value ?? "—"}</td>
                    <td className="p-1.5 border border-[#E0E6E1] break-all">{issue.responsible}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : (
            <p className="text-[10px] text-[#388E3C]">{isAdvanced ? (data.results?.process_validation_version ? "Saved automated unit checks recorded no errors; this does not verify evidence or lifecycle boundaries." : "Legacy result: current validation was not recorded.") : "All assessed values and benchmarks match the canonical unit contract; additive totals reconcile."}</p>
          )}
        </div>
      )}

      {/* Transparency */}
      <div className="p-3 rounded-lg bg-[#F8F9F8] border border-[#E0E6E1]">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#617364] mb-1">Transparency Setting</p>
        {isAdvanced ? (
          <p className="text-[11px] text-[#1B291D]">Per-substage / per-parameter transparency applied. Average reliability is reflected in Section 8.</p>
        ) : (
          <p className="text-[11px] text-[#1B291D]">Global transparency = <strong>{transparency}/10</strong> applied uniformly to all parameter scores (Raw × T/10).</p>
        )}
      </div>

      {/* Benchmarks */}
      <div className="p-3 rounded-lg bg-[#F8F9F8] border border-[#E0E6E1]">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#617364] mb-1">Benchmark Sources</p>
        <ul className="text-[11px] text-[#1B291D] list-disc list-inside space-y-0.5">
          <li>Material-based stages: country-specific benchmarks when available, else world-average.</li>
          <li>Product-based stages: best/worst benchmarks by product type ({data?.product_type || "—"}).</li>
          <li>All scoring logic is deterministic and auditable — formulas documented in the PSI methodology.</li>
        </ul>
      </div>
    </div>
  );
};
